/**
 * private-channel-auth
 *
 * Synchronizes browser auth before joining private Supabase Realtime
 * channels.
 *
 * Primary responsibilities:
 * - Read the active browser session
 * - Push the access token into Supabase Realtime
 *
 * Dependencies:
 * - @supabase/supabase-js
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { getFreshBrowserAccessToken } from '@/lib/auth/fresh-browser-session'
import type { Database } from '@/supabase/types'

type RealtimeAuthClient = Pick<SupabaseClient<Database>, 'auth' | 'realtime'>

const REALTIME_AUTH_SYNC_DEDUPE_WINDOW_MS = 30_000

let pendingRealtimeAuthSync: Promise<boolean> | null = null
let lastRealtimeAuthSync = {
  accessToken: null as string | null,
  syncedAt: 0
}

// --------------

async function syncPrivateRealtimeAuthOnce(
  supabase: RealtimeAuthClient
): Promise<boolean> {
  const accessToken = await getFreshBrowserAccessToken(supabase)

  if (!accessToken) {
    lastRealtimeAuthSync = {
      accessToken: null,
      syncedAt: 0
    }
    return false
  }

  const now = Date.now()

  if (
    lastRealtimeAuthSync.accessToken === accessToken &&
    now - lastRealtimeAuthSync.syncedAt < REALTIME_AUTH_SYNC_DEDUPE_WINDOW_MS
  ) {
    return true
  }

  await supabase.realtime.setAuth(accessToken)
  lastRealtimeAuthSync = {
    accessToken,
    syncedAt: Date.now()
  }

  return true
}

// --------------

export async function syncPrivateRealtimeAuth(
  supabase: RealtimeAuthClient
): Promise<boolean> {
  if (pendingRealtimeAuthSync) {
    return pendingRealtimeAuthSync
  }

  pendingRealtimeAuthSync = syncPrivateRealtimeAuthOnce(supabase).finally(() => {
    pendingRealtimeAuthSync = null
  })

  return pendingRealtimeAuthSync
}

// --------------

export function resetPrivateRealtimeAuthSyncForTests() {
  pendingRealtimeAuthSync = null
  lastRealtimeAuthSync = {
    accessToken: null,
    syncedAt: 0
  }
}
